import fetchApi from './utils/fetchApi.js';
import parseApiResponse from './utils/parseApiResponse.js';
import BaseClient from './BaseClient.js';
import { Headers } from 'cross-fetch';
import CryptoJS from 'crypto-js';
const { SHA256, HmacSHA256 } = CryptoJS;
export default class AppClient extends BaseClient {
    async hasUpdates(params) {
        const formData = createFormData(params);
        const response = await fetchApi({
            ...this.requestContext,
            url: this.buildAppClientUrl('has-updates'),
            method: 'POST',
            body: formData,
        });
        return await parseApiResponse(response, this.getOption('onApiError'));
    }
    async listChanged({ path, limit, cursor, specs, includeRootFiles = false, includeDefaultSpecs, includeUsersFiles, }) {
        const formData = createFormData({
            path,
            cursor,
            includeRootFiles,
            includeDefaultSpecs,
            includeUsersFiles,
        });
        specs && populateSelectors(formData, specs);
        if (limit) {
            formData.append('limit', limit + '');
        }
        const response = await fetchApi({
            ...this.requestContext,
            url: this.buildAppClientUrl('list-changed'),
            method: 'POST',
            body: formData,
        });
        return await parseApiResponse(response, this.getOption('onApiError'));
    }
    async requestAccessToken({ username, password, mfaCode, }) {
        const method = 'POST';
        const contentType = 'application/json';
        const time = new Date().toISOString();
        const cryptedPass = sha2many(password, 1000);
        const data = time + method + contentType;
        const base = '' + getHMAC(data, '' + cryptedPass);
        const headers = new Headers(this.globalHeaders);
        headers.delete('Authorization'); // we don't want duplicate auth headers
        headers.append('Content-Type', contentType);
        headers.append('X-Date', time);
        headers.append('X-Valu-Authorization', `Valu-User:${username}:${base}`);
        if (mfaCode) {
            headers.append('X-Shw-Mfa-Code', mfaCode);
        }
        // Omit existing access token to avoid duplicate authentication headers
        const { accessToken: oldToken, ...baseContext } = this.requestContext;
        const response = await fetchApi({
            ...baseContext,
            url: this.buildUrl('/rest/session/request-access-token'),
            method,
            headers,
        });
        const accessToken = await parseApiResponse(response, this.getOption('onApiError'));
        if (!accessToken) {
            throw new Error('Invalid username or password');
        }
        return accessToken;
    }
    buildAppClientUrl(action) {
        const parts = ['showell-client', 'rest'];
        const { appClientApiVersion } = this.options;
        const { accountId } = this.requestContext;
        if (accountId) {
            parts.push(accountId);
        }
        if (appClientApiVersion) {
            parts.push(appClientApiVersion);
        }
        parts.push(action);
        return this.buildUrl('/' + parts.join('/'));
    }
}
function getHMAC(message, key) {
    return HmacSHA256(message, key).toString();
}
function sha2many(message, amount) {
    let m = message;
    for (let i = 0; i < amount; i++) {
        m = SHA256(m).toString();
    }
    return m;
}
function createFormData({ path, cursor, includeRootFiles, includeDefaultSpecs, includeUsersFiles, }) {
    const formData = new FormData();
    if (cursor) {
        formData.append('cursor', cursor);
    }
    formData.append('path', path);
    if (includeRootFiles !== undefined) {
        formData.append('includeRootFiles', includeRootFiles ? '1' : '0');
    }
    if (includeDefaultSpecs !== undefined) {
        formData.append('includeDefaultSpecs', includeDefaultSpecs ? '1' : '0');
    }
    if (includeUsersFiles !== undefined) {
        formData.append('includeUsersFiles', includeUsersFiles ? '1' : '0');
    }
    return formData;
}
function populateSelectors(formData, specs, baseProp = 'specs') {
    for (let key in specs) {
        const prop = `${baseProp}[${key}]`;
        const value = specs[key];
        if (typeof value === 'boolean') {
            formData.append(prop, value ? '1' : '0');
        }
        else {
            populateSelectors(formData, value, prop);
        }
    }
}
